import React, { useState } from 'react';
import { Container, Card, Form, Button, Alert, Spinner, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import ClientNavbar from '../../components/ClientNavbar';
import clientAuthService from '../../services/clientAuthService';

const ClientChangePassword = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [showPasswords, setShowPasswords] = useState({ old: false, new: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const toggle = (field) => setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));

  const validate = () => {
    if (!formData.oldPassword) { setError('Old password is required'); return false; }
    if (!formData.newPassword) { setError('New password is required'); return false; }
    if (formData.newPassword.length < 6) { setError('New password must be at least 6 characters'); return false; }
    if (formData.newPassword !== formData.confirmPassword) { setError('New passwords do not match'); return false; }
    if (formData.oldPassword === formData.newPassword) { setError('New password must be different from old password'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      const response = await clientAuthService.changePassword(formData.oldPassword, formData.newPassword);
      if (response.message) {
        setSuccess(response.message);
        setFormData({ oldPassword: '', newPassword: '', confirmPassword: '' });
        setTimeout(() => navigate('/client/dashboard'), 2000);
      }
    } catch (err) {
      setError(err.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const getStrength = (pw) => {
    if (!pw) return { label: '', color: '' };
    let s = 0;
    if (pw.length >= 6) s++;
    if (pw.length >= 8) s++;
    if (/\d/.test(pw)) s++;
    if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) s++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(pw)) s++;
    if (s <= 2) return { label: 'Weak', color: 'danger' };
    if (s <= 3) return { label: 'Medium', color: 'warning' };
    return { label: 'Strong', color: 'success' };
  };

  const strength = getStrength(formData.newPassword);

  return (
    <>
      <ClientNavbar />
      <Container className="mt-5" style={{ maxWidth: '600px' }}>
        <Card className="shadow">
          <Card.Header className="text-white" style={{ background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)' }}>
            <h4 className="mb-0">
              <i className="fas fa-key me-2"></i>Change Password
            </h4>
          </Card.Header>
          <Card.Body>
            {success && (
              <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                <i className="fas fa-check-circle me-2"></i>{success}
              </Alert>
            )}
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError('')}>
                <i className="fas fa-exclamation-circle me-2"></i>{error}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Old Password <span className="text-danger">*</span></Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPasswords.old ? 'text' : 'password'}
                    name="oldPassword"
                    placeholder="Enter your current password"
                    value={formData.oldPassword}
                    onChange={handleInputChange}
                    disabled={loading}
                    required
                  />
                  <Button variant="outline-secondary" onClick={() => toggle('old')} disabled={loading}>
                    <i className={`fas fa-eye${showPasswords.old ? '-slash' : ''}`}></i>
                  </Button>
                </InputGroup>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>New Password <span className="text-danger">*</span></Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPasswords.new ? 'text' : 'password'}
                    name="newPassword"
                    placeholder="Enter new password (min 6 characters)"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    disabled={loading}
                    required
                    minLength={6}
                  />
                  <Button variant="outline-secondary" onClick={() => toggle('new')} disabled={loading}>
                    <i className={`fas fa-eye${showPasswords.new ? '-slash' : ''}`}></i>
                  </Button>
                </InputGroup>
                {formData.newPassword && (
                  <div className="mt-2">
                    <small className="text-muted">Password Strength: </small>
                    <span className={`badge bg-${strength.color}`}>{strength.label}</span>
                  </div>
                )}
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label>Confirm New Password <span className="text-danger">*</span></Form.Label>
                <InputGroup>
                  <Form.Control
                    type={showPasswords.confirm ? 'text' : 'password'}
                    name="confirmPassword"
                    placeholder="Re-enter new password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    disabled={loading}
                    required
                  />
                  <Button variant="outline-secondary" onClick={() => toggle('confirm')} disabled={loading}>
                    <i className={`fas fa-eye${showPasswords.confirm ? '-slash' : ''}`}></i>
                  </Button>
                </InputGroup>
                {formData.newPassword && formData.confirmPassword && (
                  <div className="mt-2">
                    {formData.newPassword === formData.confirmPassword
                      ? <small className="text-success"><i className="fas fa-check me-1"></i>Passwords match</small>
                      : <small className="text-danger"><i className="fas fa-times me-1"></i>Passwords do not match</small>}
                  </div>
                )}
              </Form.Group>

              <div className="mb-4 p-3 border border-info rounded bg-light">
                <small className="text-muted">
                  <strong>Password Requirements:</strong>
                  <ul className="mb-0 mt-2">
                    <li>Minimum 6 characters long</li>
                    <li>Different from your old password</li>
                    <li>Recommended: Include uppercase, lowercase, numbers, and special characters</li>
                  </ul>
                </small>
              </div>

              <div className="d-flex gap-2">
                <Button variant="primary" type="submit" disabled={loading} className="flex-grow-1">
                  {loading
                    ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Changing Password...</>
                    : <><i className="fas fa-lock me-2"></i>Change Password</>}
                </Button>
                <Button variant="secondary" onClick={() => navigate('/client/dashboard')} disabled={loading}>
                  <i className="fas fa-times me-2"></i>Cancel
                </Button>
              </div>
            </Form>

            <div className="mt-4 p-3 border border-warning rounded bg-light">
              <small className="text-muted">
                <i className="fas fa-shield-alt me-2 text-warning"></i>
                <strong>Security Tip:</strong> Choose a strong password that you haven't used before.
                Never share your password with anyone.
              </small>
            </div>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default ClientChangePassword;
